import React from "react"
import { Form, Button, TimePicker, Row, Col } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import { useDispatch } from "react-redux"
import { updatedAvailableHours } from "store/trips"

export const TimeBlockForm = ({ form }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const onFieldsChange = () => {
    const updated = form.getFieldValue("available_hours")
    dispatch(updatedAvailableHours(updated))
  }

  const handleAdd = () => {
    const current = form.getFieldValue("available_hours") || []
    const last = current.at(-1)

    if (last?.start && last?.end) {
      const newList = [...current, { start: null, end: null }]
      form.setFieldsValue({ available_hours: newList })
      dispatch(updatedAvailableHours(newList))
    }
  }

  const validateStartTime = (form, t, name) => async (_, startValue) => {
    const allBlocks = form.getFieldValue("available_hours") || []
    const currentBlock = allBlocks[name]
    const endValue = currentBlock?.end

    if (!startValue || !endValue) return

    if (startValue.isSame(endValue)) {
      throw new Error(t("experiences.scheduling.errors.same_start_end"))
    }

    const isDuplicate = allBlocks.some((block, idx) => idx !== name && block?.start?.isSame(startValue) && block?.end?.isSame(endValue))

    if (isDuplicate) {
      throw new Error(t("experiences.scheduling.errors.duplicate_time_block"))
    }
  }

  const validateEndTime = (form, t, name) => async (_, endValue) => {
    const allBlocks = form.getFieldValue("available_hours") || []
    const currentBlock = allBlocks[name]

    if (!currentBlock?.start || !endValue) return

    if (currentBlock.start.isSame(endValue)) {
      throw new Error(t("experiences.scheduling.errors.same_start_end"))
    }

    const isDuplicate = allBlocks.some(
      (block, idx) => idx !== name && block?.start?.isSame(currentBlock.start) && block?.end?.isSame(endValue)
    )

    if (isDuplicate) {
      throw new Error(t("experiences.scheduling.errors.duplicate_time_block"))
    }
  }
  return (
    <>
      <Text className="block mb-s" fw="500">
        {t("experiences.scheduling.labels.time_blocks")}
      </Text>

      <Form.List name="available_hours">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Row key={key} gutter={16} align="middle" style={{ alignItems: "center" }}>
                <Col span={10}>
                  <Form.Item
                    {...restField}
                    name={[name, "start"]}
                    style={{ marginBottom: 5, marginTop: 5 }}
                    rules={[
                      { required: true, message: t("experiences.scheduling.errors.start_time") },
                      {
                        validator: (...args) => validateStartTime(form, t, name)(...args)
                      }
                    ]}
                  >
                    <TimePicker
                      size="large"
                      format="hh:mm a"
                      use12Hours
                      style={{ width: "100%" }}
                      needConfirm={false}
                      onChange={onFieldsChange}
                    />
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item
                    {...restField}
                    name={[name, "end"]}
                    style={{ marginBottom: 5, marginTop: 5 }}
                    rules={[
                      { required: true, message: t("experiences.scheduling.errors.end_time") },
                      {
                        validator: (...args) => validateEndTime(form, t, name)(...args)
                      }
                    ]}
                  >
                    <TimePicker
                      size="large"
                      format="hh:mm a"
                      use12Hours
                      style={{ width: "100%" }}
                      onChange={onFieldsChange}
                      needConfirm={false}
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  {fields?.length > 1 && (
                    <Icon
                      name="Trash"
                      color={colors.textSecondary}
                      width={20}
                      height={20}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        remove(name)
                        setTimeout(onFieldsChange, 0)
                      }}
                    />
                  )}
                </Col>
              </Row>
            ))}
            <Form.Item>
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => {
                  const current = form.getFieldValue("available_hours") || []
                  if (current.length > 0) {
                    handleAdd()
                  } else {
                    add()
                    setTimeout(onFieldsChange, 0)
                  }
                }}
              >
                {t("experiences.scheduling.labels.add_time_blocks")}
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </>
  )
}

TimeBlockForm.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired
  }).isRequired
}
